import { useState, useRef, useCallback, useEffect } from 'react'
import { Loader2, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { formatUserDisplayName } from '@/utils/formatters'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { useUserSearch } from '@/hooks/useUserSearch'
import {
  useAdminPlanShares,
  useCreateAdminPlanShare,
  useDeleteAdminPlanShare,
} from '@/hooks/useAdminPlans'
import type { UserRead } from '@/api/shares'
import type { ApiPlanSummary } from '@/types'

interface ManageSharesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: ApiPlanSummary | null
}

export function ManageSharesDialog({
  open,
  onOpenChange,
  plan,
}: ManageSharesDialogProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserRead | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const planId = plan?.id

  // Hooks
  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query: searchQuery })
  const { data: sharesData, isLoading: isLoadingShares } =
    useAdminPlanShares(planId)
  const createShareMutation = useCreateAdminPlanShare(planId)
  const deleteShareMutation = useDeleteAdminPlanShare(planId)

  // Flatten pages to get all users
  const allUsers = searchData?.pages.flatMap(page => page.users) ?? []

  // Filter out users who already have access
  const existingUserIds = new Set<string>()
  if (sharesData?.owner) existingUserIds.add(sharesData.owner.id)
  sharesData?.items.forEach(share => {
    if (share.user) existingUserIds.add(share.user.id)
  })
  const users = allUsers.filter(u => !existingUserIds.has(u.id))

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setSelectedUser(null)
    setShowDropdown(true)
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true)
    }
  }

  const handleSelectUser = (user: UserRead) => {
    setSelectedUser(user)
    setSearchQuery(user.email || user.display_name || '')
    setShowDropdown(false)
  }

  const handleAddShare = () => {
    if (!selectedUser) return

    createShareMutation.mutate(
      { userId: String(selectedUser.id) },
      {
        onSuccess: () => {
          setSelectedUser(null)
          setSearchQuery('')
        },
      }
    )
  }

  const handleRemoveShare = (shareId: string) => {
    deleteShareMutation.mutate({ shareId })
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedUser(null)
      setShowDropdown(false)
    }
    onOpenChange(isOpen)
  }

  const shouldShowDropdown = showDropdown && searchQuery.length >= 2

  if (!plan) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Manage Shares</DialogTitle>
          <DialogDescription>
            Manage who has access to &ldquo;{plan.name}&rdquo;
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Add user section */}
          <div className="flex gap-2">
            <div className="relative flex-1" ref={dropdownRef}>
              <Input
                ref={inputRef}
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className="w-full"
              />

              {shouldShowDropdown && (
                <div
                  ref={listRef}
                  onScroll={handleScroll}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
                >
                  {isSearching && !isFetchingNextPage ? (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  ) : users.length > 0 ? (
                    <ul className="py-1">
                      {users.map(user => {
                        const colors = getAvatarColor(user.id)
                        return (
                          <li key={user.id}>
                            <button
                              type="button"
                              onClick={() => handleSelectUser(user)}
                              className={cn(
                                'w-full flex items-center gap-3 px-3 py-2',
                                'hover:bg-muted cursor-pointer',
                                'text-left transition-colors duration-150',
                                selectedUser?.id === user.id && 'bg-muted'
                              )}
                            >
                              <Avatar className="h-8 w-8">
                                <AvatarFallback
                                  className={cn(
                                    colors.bg,
                                    colors.text,
                                    'text-xs font-medium'
                                  )}
                                >
                                  {getInitials(user.display_name, user.email)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.display_name || user.email}
                                </p>
                                {user.display_name && user.email && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {user.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                      {isFetchingNextPage && (
                        <li className="px-4 py-3 text-center">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-muted-foreground">
                      <span className="text-sm">
                        No users found for &ldquo;{searchQuery}&rdquo;
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={handleAddShare}
              disabled={!selectedUser || createShareMutation.isPending}
              className="bg-primary hover:bg-primary-hover"
            >
              {createShareMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Add'
              )}
            </Button>
          </div>

          {/* People with access */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">People with access</h3>
            {isLoadingShares ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Owner */}
                {sharesData?.owner && (
                  <UserRow
                    userId={sharesData.owner.id}
                    displayName={sharesData.owner.display_name}
                    email={sharesData.owner.email}
                    role="Owner"
                  />
                )}

                {/* Editors */}
                {sharesData?.items.map(share => {
                  if (!share.user) return null
                  return (
                    <UserRow
                      key={share.id}
                      userId={share.user.id}
                      displayName={share.user.display_name}
                      email={share.user.email}
                      role="Editor"
                      onRemove={() => handleRemoveShare(share.id)}
                      isRemoving={deleteShareMutation.isPending}
                    />
                  )
                })}

                {/* Empty state */}
                {!sharesData?.owner && sharesData?.items.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No one has access yet
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface UserRowProps {
  userId: string
  displayName: string | null
  email: string | null
  role: 'Owner' | 'Editor'
  onRemove?: () => void
  isRemoving?: boolean
}

function UserRow({
  userId,
  displayName,
  email,
  role,
  onRemove,
  isRemoving,
}: UserRowProps) {
  const colors = getAvatarColor(userId)

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8">
          <AvatarFallback
            className={cn(colors.bg, colors.text, 'text-xs font-medium')}
          >
            {getInitials(displayName, email)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium">
            {formatUserDisplayName({
              id: userId,
              display_name: displayName,
              email,
            })}
          </p>
          {displayName && email && (
            <p className="text-xs text-muted-foreground">{email}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{role}</span>
        {role === 'Editor' && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 cursor-pointer"
            onClick={onRemove}
            disabled={isRemoving}
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}
